import cx from 'classnames';
import {DOMNode, domToReact, HTMLReactParserOptions, Element as ParserElement} from 'html-react-parser';
import {filter, last, omit, takeRight} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    ClientDynamicContent, Icon, NodeElement, notifier, SessionActionControl,
    Button, TextChat, SubtitlesMessages,
    ClientIdleReminder
} from 'components';
import {DynamicComponentNames, AvatarUnits as Units, AVATAR_DEFAULT_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {WebRTCServerSource} from 'enums';
import {useAudioStreamRecord, useClientSession, useLocalStreams} from 'hooks';
import {SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ClientSessionTextChat} from 'pages/Client/TextChat';
import {DefaultParticipant, TextChatContentType, TextChatMessage} from 'types';
import {buildTranslationKey, dynamicContentUtils, logger, sessionUtils} from 'utils';

import {Avatar} from './Avatar';

import './styles.scss';

const CLIENT_MESSAGE_LIVE_TIME = 5000;
const MAX_SUBTITLES_MESSAGES_COUNT = 2;
const MAX_AUDIO_RECONNECTS = 3;
const T_KEYS = TRANSLATION_KEYS.COMPONENTS.AVATAR;

const T_KEYS_CONTROLS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.IN_SESSION_VIEW.CONTROLS;
const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        renderAvatar, renderControls, renderSubtitles, onMouseOver, onMouseOut, onTouchStart,
        localParticipant
    } = props;

    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.Avatar, node)) {
                return <NodeElement node={node}>{renderAvatar()}</NodeElement>;
            }

            if (isMatchElement(Units.Controls, node)) {
                return <NodeElement node={node}>{renderControls()}</NodeElement>;
            }

            if (isMatchElement(Units.Subtitles, node)) {
                return <NodeElement node={node}>{renderSubtitles()}</NodeElement>;
            }

            if (isMatchElement(Units.Container, node)) {
                const element = node as ParserElement;
                const options = getOptions(props);
                return (
                    <NodeElement
                        node={element}
                        onMouseOver={onMouseOver}
                        onMouseOut={onMouseOut}
                    >
                        <Fragment>
                            {domToReact(element.children as DOMNode[], options)}
                            <div className='blackout' onTouchStart={onTouchStart} />
                        </Fragment>
                    </NodeElement>
                );
            }

            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const AvatarView: React.FC = () => {
    const {t} = useTranslation();
    const reconnectAttempts = useRef(0);
    const audioStreamNotRecordedWarned = useRef(false);
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const [isAudioEnabled, setIsAudioEnabled] = useState(true);
    const [lastSpokenMessageId, setLastSpokenMessageId] = useState<string | null>(null);

    const {
        socket, avatarThreads, onSubmitMessage, onChangeTextChatActivityState, setMediaDevices, endLocalSession,
        setTracks, notifyWebRTCStartSuccess, notifyWebRTCStartFailed
    } = useContext(ClientContext);

    const {
        session, avatarActivity, participantId, audioStreamEnabled, isPreCall, transcriptionLanguage,
        controlsAutoHideEnabled, controlsAutoHideTimeout, controlsInitialVisible, subtitlesEnabled,
        avatarHost, avatarUrl, avatarZoom, avatarBackgroundZoom, avatarBackgroundImageUrl, intermediateMessages
    } = useClientSession();

    const [isActive, setIsActive] = useState(controlsInitialVisible);
    const [showSubtitles, setShowSubtitles] = useState(subtitlesEnabled);

    const isAvatarActive = useMemo(() => avatarLoaded && !!avatarActivity, [avatarActivity, avatarLoaded]);

    const shouldRecordAudioStream = useMemo(() =>
        audioStreamEnabled && isPreCall && socket?.connected && isAvatarActive,
    [audioStreamEnabled, isPreCall, socket?.connected, isAvatarActive]);

    const mediaConstraints = useMemo(() => ({
        audioinput: isAvatarActive,
    }), [isAvatarActive]);

    const {audioTrack, tracks, error: audioTracksError, retryConnection} = useLocalStreams({
        enabled: isAvatarActive,
        mediaConstraints,
        onEnumerateMediaDevices: setMediaDevices,
        onEnumerateTracks: setTracks,
        // TODO: do we need callbacks?
        onSuccess: () => {
            notifyWebRTCStartSuccess?.(WebRTCServerSource.Internal);
        },
        onError: (err) => {
            setIsAudioEnabled(false);
            notifyWebRTCStartFailed?.(WebRTCServerSource.Internal, {
                code: err.name,
                message: err.message,
                attempt: 1,
                internal: true
            });
        }
    });

    const recentChatbotMessage: TextChatMessage = useMemo(() => {
        const chatbotMessages = sessionUtils.getTextChatActivityMessages({
            [DefaultParticipant.Chatbot]:
                last(avatarThreads?.[DefaultParticipant.Chatbot]) ? [last(avatarThreads[DefaultParticipant.Chatbot])] : [],
            [DefaultParticipant.System]:
                last(avatarThreads?.[DefaultParticipant.System]) ? [last(avatarThreads[DefaultParticipant.System])] : [],
        });
        return last(chatbotMessages);
    }, [avatarThreads]);

    const recentChatbotMessageWithContent = useMemo(() => {
        if (!recentChatbotMessage) {
            return null;
        }
        let content = recentChatbotMessage.content;
        if (recentChatbotMessage.contentType === TextChatContentType.InteractiveChatError) {
            content = t(T_KEYS.INTERACTIVE_CHAT_CODE.CHATBOT_UNAVAILABLE_MESSAGE);
        }
        if (recentChatbotMessage.contentType === TextChatContentType.InteractiveChat) {
            const tKey = T_KEYS.INTERACTIVE_CHAT_CODE[buildTranslationKey(recentChatbotMessage.content)];
            if (tKey) {
                content = t(tKey);
            }
        }
        return {...recentChatbotMessage, content};
    }, [recentChatbotMessage, t]);

    const recentClientMessage: TextChatMessage = useMemo(() => {
        const clientMessages = sessionUtils.getTextChatActivityMessages(
            omit(avatarThreads, [DefaultParticipant.Chatbot, DefaultParticipant.System])
        );
        return last(clientMessages);
    }, [avatarThreads]);

    const [subtitlesMessages, setSubtitleMessages] = useState<{id: string, participantName: string, text: string}[]>([]);

    const onMuteToggle = useCallback(() => {
        setIsAudioEnabled((prev) => {
            if (audioTrack) {
                audioTrack.enabled = !prev;
            }
            return !prev;
        });

        if (!audioTrack && !isAudioEnabled) {
            retryConnection();
        }
    }, [audioTrack, isAudioEnabled, retryConnection]);

    useAudioStreamRecord({
        shouldRecord: shouldRecordAudioStream,
        socket,
        sessionId: session.id,
        participantId,
        activityId: avatarActivity?.id,
        tracks,
        transcriptionLanguage
    });

    const handleLoadCompleted = useCallback(() => {
        setAvatarLoaded(true);
    }, []);

    const handleSpeakStarted = useCallback((text: string, meta: {id: string}) => {
        setSubtitleMessages((prev) => {
            const newMessage = {
                id: meta.id,
                participantName: t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.ASSISTANT),
                text,
            };
            return takeRight([...prev, newMessage], MAX_SUBTITLES_MESSAGES_COUNT);
        });
    }, [t]);

    const handleSpeakFinished = useCallback((text: string, meta: {id: string}) => {
        setSubtitleMessages((prev) => filter(prev, (m) => m.id !== meta.id));
        setLastSpokenMessageId(meta.id);
    }, []);

    const renderAvatar = useCallback(() => (
        <Avatar
            host={avatarHost}
            url={avatarUrl}
            avatarZoom={avatarZoom}
            backgroundZoom={avatarBackgroundZoom}
            backgroundImageUrl={avatarBackgroundImageUrl}
            intermediateMessages={intermediateMessages}
            recentClientMessage={recentClientMessage}
            message={recentChatbotMessageWithContent}
            onSpeakStarted={handleSpeakStarted}
            onSpeakFinished={handleSpeakFinished}
            onLoadCompleted={handleLoadCompleted}
        />
    ), [avatarHost, avatarUrl, avatarZoom, avatarBackgroundZoom, avatarBackgroundImageUrl, recentClientMessage, intermediateMessages,
        recentChatbotMessageWithContent, handleSpeakStarted, handleSpeakFinished, handleLoadCompleted]);

    const handleMouseOver = useCallback(() => controlsAutoHideEnabled && setIsActive(true), [controlsAutoHideEnabled]);
    const handleMouseOut = useCallback(() => controlsAutoHideEnabled && setIsActive(false), [controlsAutoHideEnabled]);
    const handleTouch = useCallback(() => controlsAutoHideEnabled && setIsActive((prev) => !prev), [controlsAutoHideEnabled]);

    const onNewUnreadMessage = useCallback(() => {
        if (!isActive) {
            setIsActive(true);
        }
    }, [isActive]);

    const renderControls = useCallback(() => (
        <div className='bottom-actions'>
            <div className={cx('in-session-actions', {
                active: isActive
            })}
            >
                <div className='left actions-section'>
                    <div className='vs-audio-icon'>
                        <SessionActionControl
                            label={t(T_KEYS_CONTROLS.MUTE)}
                            isDisabled={!isAudioEnabled}
                            onClick={onMuteToggle}
                        >
                            <Icon name='microphone' size='3xl' />
                        </SessionActionControl>
                    </div>
                </div>

                <div className='center actions-section'>
                    <div className='vs-end-call-icon'>
                        <Button
                            color='attention'
                            width='auto'
                            height='auto'
                            rounded
                            onClick={endLocalSession}
                        >
                            <Icon name='phone-end' size='3xl' />
                        </Button>
                        <div className='icon-text'>{t(T_KEYS_CONTROLS.LEAVE)}</div>
                    </div>
                </div>

                <div className='right actions-section'>
                    {subtitlesEnabled && (
                        <div>
                            <SessionActionControl
                                label={t(T_KEYS_CONTROLS.SUBTITLES)}
                                isDisabled={!showSubtitles}
                                onClick={() => setShowSubtitles(!showSubtitles)}
                            >
                                <Icon name='file-text' size='lg' color='light' />
                            </SessionActionControl>
                        </div>
                    )}
                    <ClientIdleReminder
                        lastSpokenMessageId={lastSpokenMessageId}
                        session={session}
                        onSubmitMessage={onSubmitMessage}
                    />
                    <TextChat
                        TextChatComponent={ClientSessionTextChat}
                        expandable={false}
                        session={session}
                        hideUnreadMessagesIcon
                        disableNewMessageSound
                        downloadFileDisabled
                        uploadFileHidden
                        activityName={SessionActivityName.Avatar}
                        iconSize='3xl'
                        onSubmitMessage={onSubmitMessage}
                        onChangeTextChatActivityState={onChangeTextChatActivityState}
                        onNewUnreadMessage={onNewUnreadMessage}
                    />
                </div>
            </div>
        </div>
    ), [isActive, t, isAudioEnabled, onMuteToggle, endLocalSession, subtitlesEnabled, showSubtitles, session,
        onSubmitMessage, onChangeTextChatActivityState, onNewUnreadMessage, lastSpokenMessageId]);

    const renderSubtitles = useCallback(() => (
        subtitlesEnabled && showSubtitles && avatarActivity &&
            <div className={cx('subtitles-container', {'controls-active': isActive})}>
                <SubtitlesMessages
                    messages={subtitlesMessages}
                />
            </div>
    ), [subtitlesEnabled, showSubtitles, avatarActivity, isActive, subtitlesMessages]);

    useEffect(() => {
        if (audioTracksError && reconnectAttempts.current < MAX_AUDIO_RECONNECTS) {
            retryConnection().finally(() => {
                reconnectAttempts.current++;
            });
        }
    }, [audioTracksError, retryConnection]);

    useEffect(() => {
        if (audioTracksError && reconnectAttempts.current >= MAX_AUDIO_RECONNECTS) {
            notifier.error({details: audioTracksError.message || audioTracksError.toString()});
        }
    }, [audioTracksError, retryConnection]);

    useEffect(() => {
        if (lastSpokenMessageId && !shouldRecordAudioStream && !audioStreamNotRecordedWarned.current) {
            audioStreamNotRecordedWarned.current = true;
            logger.warn('[Avatar] Client audio stream is not recorded');
        }
    }, [lastSpokenMessageId, shouldRecordAudioStream, t]);

    useEffect((() => {
        if (controlsAutoHideEnabled) {
            const resetInitialActiveTimer = setTimeout(() => {
                setIsActive(false);
            }, controlsAutoHideTimeout);

            return () => {
                clearTimeout(resetInitialActiveTimer);
            };
        }
    }));

    useEffect(() => {
        if (!recentClientMessage?.id || !recentClientMessage?.meta?.isTranscription) {
            return;
        }

        const timeout = setTimeout(() => {
            setSubtitleMessages((prev) => prev.filter((m) => m.id !== recentClientMessage.id));
        }, CLIENT_MESSAGE_LIVE_TIME);

        setSubtitleMessages((prev) => {
            const newMessage = {
                id: recentClientMessage.id,
                participantName: t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.YOU),
                text: recentClientMessage.content
            };

            return takeRight([...prev, newMessage], MAX_SUBTITLES_MESSAGES_COUNT);
        });

        return () => {
            setSubtitleMessages((prev) => prev.filter((m) => m.id !== recentClientMessage.id));
            clearTimeout(timeout);
        };
    }, [recentClientMessage?.id, recentClientMessage?.content, recentClientMessage?.meta?.isTranscription, t]);

    useEffect(() => {
        // TODO: remove after testing
        if (recentChatbotMessageWithContent) {
            logger.debug('[Avatar] last chatbot message content:', recentChatbotMessageWithContent);
        }
    }, [recentChatbotMessageWithContent]);

    useEffect(() => {
        // TODO: remove after testing
        if (recentClientMessage?.content) {
            logger.debug('[Avatar] last client message content:', recentClientMessage?.content);
        }
    }, [recentClientMessage?.content]);

    return (
        <ClientDynamicContent
            component={DynamicComponentNames.Avatar}
            defaultTemplate={AVATAR_DEFAULT_TEMPLATE}
            optionsProps={{
                renderAvatar,
                renderControls,
                renderSubtitles,
                onMouseOver: handleMouseOver,
                onMouseOut: handleMouseOut,
                onTouchStart: handleTouch
            }}
            optionsGetter={getOptions}
        />
    );
};